import { SOURCES } from './sources';

export const VEGETABLES = [
  {
    slug: "kartofler",
    shortTitle: "Kartofler",
    title: "Kan man fryse kartofler?",
    metaTitle: "Kan man fryse kartofler? Rå, kogte og mos — Fakta & råd",
    metaDescription: "Frys aldrig rå kartofler, da de bliver søde og svampede. Kartoffelmos og flødekartofler fryser derimod glimrende. Læs reglerne her.",
    answer: "Nej, rå kartofler kan ikke fryses, men kartoffelmos, flødekartofler og tilberedte kartoffelretter tåler frysning udmærket.",
    steps: [
      "Frys aldrig rå kartofler; stivelsen nedbrydes og kartoflen bliver mørk, vandet og sød jf. Arla.",
      "Opbevar rå kartofler tørt, mørkt og køligt (ikke i køleskab jf. Fødevarestyrelsen og Arla).",
      "Kartoffelmos lavet med smør eller mælk fryser fint jf. Arlas kartoffelguide.",
      "Flødekartofler og færdige retter med kartofler kan fryses i 1–3 måneder jf. Forbrugerrådet Tænk.",
      "Varm frosne kartoffelretter op direkte fra frossen tilstand i ovn eller gryde."
    ],
    tableNote: "Kilde: Arla, Fødevarestyrelsen og Forbrugerrådet Tænk",
    table: [
      { food: "Rå kartofler (hele eller skrællede)", duration: "Uegnet til frysning", pack: "Mister konsistens", thaw: "Bliver mørke, vandede og søde", use: "Opbevares mørkt og køligt, ikke i køleskab jf. Arla" },
      { food: "Kartoffelmos (med smør/mælk)", duration: "1–3 måneder", pack: "Tæt boks eller flad pose", thaw: "Direkte i gryde ved svag varme", use: "Kan fryses efter tilberedning jf. Arla; varmes i gryde" },
      { food: "Flødekartofler (tilberedte)", duration: "1–3 måneder", pack: "Ildfast form eller boks", thaw: "Køleskab før ovn", use: "Færdigretter holder 1–3 måneder på frost jf. Tænk" },
      { food: "Kogte hele kartofler", duration: "Uegnet til hel frys", pack: "Tætsluttende pose", thaw: "Køleskab", use: "Bliver melede og vandede; mos dem i stedet" },
      { food: "Kartoffelsalat (med creme fraiche)", duration: "Uegnet til frost", pack: "Dressingen skiller fuldstændigt", thaw: "Vandet og klumpet", use: "Bør spises frisk fra køleskab" }
    ],
    sections: [
      {
        h2: "Hvorfor kan rå kartofler ikke fryses?",
        body: `Arlas kartoffelguide slår fast, at rå kartofler ikke egner sig til frysning. Kartofler indeholder store mængder stivelse og vand. Ved frostgrader aktiveres enzymer, der nedbryder stivelsen til simple sukkerarter.

Når en rå kartoffel tør op, vil den smage mærkeligt sødt, farven bliver grumset og mørk, og cellestrukturen kollapser, så kartoflen føles som en våd svamp. Rå kartofler skal opbevares mørkt, tørt og køligt – men hverken Arla eller Fødevarestyrelsen anbefaler at lægge dem i køleskabet.`
      },
      {
        h2: "Kartoffelmos og tilberedte kartoffelretter",
        body: `Ifølge Arla kan kartofler sagtens fryses efter tilberedning, for eksempel som kartoffelmos eller i ovnbagte retter.

Forbrugerrådet Tænk angiver en generel holdbarhed for færdige retter på 1–3 måneder i fryseren. Frys kartoffelmosen ned i en boks eller frysepose, og varm den op direkte i en gryde ved svag varme med en klat smør eller en skvæt mælk under omrøring.`
      },
      {
        h2: "Kogte kartofler og flødekartofler",
        body: `Kogte hele kartofler får ofte en kedelig, melet struktur på frost, fordi cellevæggene sprænges, så stivelsen slipper vandet. Vil du gemme en rest kogte kartofler, er det bedre at mose dem først eller bruge dem i en tilberedt ret som flødekartofler før frysning.`
      }
    ],
    faq: [
      { q: "Må man fryse rå kartofler?", a: "Nej, rå kartofler egner sig ikke til frysning jf. Arla; stivelsen omdannes til sukker, og kartoflen bliver sød, mørk og svampet." },
      { q: "Kan man fryse kartoffelmos?", a: "Ja, kartofler kan fryses efter tilberedning som mos jf. Arla, og færdigretter holder 1–3 måneder på frost ifølge Forbrugerrådet Tænk." },
      { q: "Hvor bør rå kartofler opbevares?", a: "Rå kartofler skal opbevares tørt, mørkt og køligt, men ikke i køleskab, ifølge Fødevarestyrelsen og Arla." },
      { q: "Hvorfor bliver kogte kartofler vandede i fryseren?", a: "Frostkrystallerne ødelægger cellerne, så vandet skilles fra stivelsen ved optøning." }
    ],
    internalLinks: [
      { text: "Hvad kan man ikke fryse?", href: "/hvad-kan-man-ikke-fryse" },
      { text: "Sådan fryser du mad rigtigt", href: "/saadan-fryser-du-mad" },
      { text: "Holdbarhed i fryseren for grønt og retter", href: "/hvor-laenge-holder-mad-i-fryseren" },
      { text: "Frysning af færdigretter", href: "/faerdigretter" },
      { text: "Frysning af saucer og salater", href: "/saucer-og-salater" }
    ],
    sources: [
      SOURCES.arlaKartofler,
      SOURCES.foedevarestyrelsenOpbevaring,
      SOURCES.taenkHoldbarhed
    ]
  },
  {
    slug: "tomater",
    shortTitle: "Tomater og tomatpuré",
    title: "Kan man fryse tomater?",
    metaTitle: "Kan man fryse tomater og tomatpuré? — Læs før du fryser",
    metaDescription: "Ja, hele tomater kan fryses og er oplagte til kødsovs, suppe og gryderetter jf. Fødevarestyrelsen og Samvirke. Se frysetricks her.",
    answer: "Ja, men frosne tomater mister al sprødhed og kan udelukkende bruges i varme retter som kødsovs, suppe og gryderetter.",
    steps: [
      "Fødevarestyrelsen anbefaler at fryse bløde tomater ned til brug i kødsovs eller dahl.",
      "Samvirke råder til at gemme bløde tomater i en frysepose; frys gerne basilikumblade i samme pose.",
      "Frys tomaterne hele; når de holdes kort under den lunkne vandhane, glider skindet let af med fingrene.",
      "Frys overskydende tomatpuré i en isterningbakke i portioner á 1 spiseskefuld.",
      "Brug aldrig optøede tomater i rå salater, da de bliver bløde og vandede."
    ],
    tableNote: "Kilde: Fødevarestyrelsen, Samvirke og Forbrugerrådet Tænk",
    table: [
      { food: "Friske hele tomater", duration: "6–12 måneder", pack: "Bageplade først, derefter pose", thaw: "Under rindende vand / i gryde", use: "Grøntsager holder 6–12 mdr. jf. Tænk; bruges i kødsovs/suppe jf. Fødevarestyrelsen" },
      { food: "Tomater og basilikum i pose", duration: "6–12 måneder", pack: "Frysepose", thaw: "Direkte i gryde", use: "Samvirke anbefaler at fryse bløde tomater og basilikum sammen til tomatsovs" },
      { food: "Tomatpuré (åbnet)", duration: "Forlænger holdbarhed", pack: "Isterningbakke eller lille boks", thaw: "Direkte i varm ret", use: "Giver fylde og umami i kødsovs og saucer" },
      { food: "Hjemmelavet tomatsauce / ret", duration: "1–3 måneder", pack: "Frysepose uden luft", thaw: "Køleskab eller gryde", use: "Færdige retter holder 1–3 mdr. jf. Tænk" }
    ],
    sections: [
      {
        h2: "Fødevarestyrelsens og Samvirkes råd om tomater",
        body: `Fødevarestyrelsen fremhæver specifikt tomater som en blød grøntsag, man med fordel kan komme i fryseren og bruge i kødsovs eller dahl.

Samvirke supplerer med et konkret madspildstip: Er tomaterne blevet for bløde til salaten, gemmes de i en frysepose i fryseren til en omgang spaghetti med tomatsovs. Du kan med fordel fryse triste basilikumblade i samme pose. I køleskab mister rå tomater smag og modning; de opbevares bedst på køkkenbordet, hvor de holder 5–6 dage ifølge Forbrugerrådet Tænk.`
      },
      {
        h2: "Det smarte trick: Smut skindet på 5 sekunder",
        body: `Friske tomater indeholder op mod 94 % vand. Frosten sprænger cellevæggene, så tomaten falder sammen ved optøning. Det gør dem uegnede til salat, men perfekte til varme saucer.

Tag en frossen tomat direkte fra fryseren og hold den under den lunkne vandhane i få sekunder. Du kan nu gribe fat i skindet med fingrene og trække det af i ét stykke. Hak derefter tomaten groft og kom den direkte i gryden.`
      },
      {
        h2: "Holdbarhed på frost",
        body: `Ifølge Forbrugerrådet Tænks holdbarhedstabel holder grøntsager i fryseren i 6–12 måneder afhængig af emballage og type. Hjemmelavede færdigretter med tomatsauce holder 1–3 måneder.`
      }
    ],
    faq: [
      { q: "Kan man bruge frosne tomater i en rå salat?", a: "Nej, frosne tomater mister deres spændstighed og bliver bløde og vandede. Brug dem i varme retter." },
      { q: "Hvad anbefaler Fødevarestyrelsen at bruge frosne tomater til?", a: "Fødevarestyrelsen foreslår at fryse bløde tomater og anvende dem i kødsovs eller dahl." },
      { q: "Kan man fryse basilikum sammen med tomaterne?", a: "Ja, Samvirke anbefaler at fryse bløde tomater og triste basilikumblade i samme frysepose til senere tomatsovs." },
      { q: "Hvor længe kan grøntsager holde sig i fryseren?", a: "Grøntsager holder sig i 6–12 måneder på frost ifølge Forbrugerrådet Tænk." }
    ],
    internalLinks: [
      { text: "Sådan fryser du mad rigtigt", href: "/saadan-fryser-du-mad" },
      { text: "Holdbarhedstabeller for grøntsager på frost", href: "/hvor-laenge-holder-mad-i-fryseren" },
      { text: "Frysning af squash og agurk", href: "/squash-og-agurk" },
      { text: "Frysning af løg og hvidløg", href: "/loeg-hvidloeg-ingefaer-chili" },
      { text: "Frysning af friske krydderurter", href: "/krydderurter" }
    ],
    sources: [
      SOURCES.foedevarestyrelsenOpbevaring,
      SOURCES.samvirkeRester,
      SOURCES.taenkHoldbarhed
    ]
  },
  {
    slug: "squash-og-agurk",
    shortTitle: "Squash og agurk",
    title: "Kan man fryse squash og agurk?",
    metaTitle: "Kan man fryse squash og agurk? — Rå, revet og skiver",
    metaDescription: "Agurk tåler ikke frysning pga. højt vandindhold. Squash kan fryses til kødsovs og gryderetter jf. Fødevarestyrelsen. Læs rådene her.",
    answer: "Ja til squash, men nej til agurk: Agurk bliver fuldstændig slimet og uspiselig, mens squash fryser fint og kan bruges i kødsovs, dahl og gryderetter.",
    steps: [
      "Frys aldrig almindelig salatagurk; cellevæggene sprænges og agurken bliver en slimet, vandet masse.",
      "Fødevarestyrelsen fremhæver squash som en blød grøntsag, der fint kan fryses og bruges i kødsovs eller dahl.",
      "Riv squash groft på et rivejern, eller skær den i tern, og pak i fryseposer.",
      "Tilsæt frossen squash direkte til kødsovs, gryderetter eller kagedej.",
      "Hold agurker i køleskabet i original emballage, hvor de holder cirka 1 uge ifølge Tænk."
    ],
    tableNote: "Kilde: Fødevarestyrelsen og Forbrugerrådet Tænk",
    table: [
      { food: "Squash (revet eller i tern)", duration: "6–12 måneder", pack: "Flad frysepose", thaw: "Direkte i varm ret / kagedej", use: "Grøntsager holder 6–12 mdr. jf. Tænk; bruges i kødsovs/dahl jf. Fødevarestyrelsen" },
      { food: "Agurk (frisk)", duration: "Uegnet til frost", pack: "Cellerne sprænges fuldstændigt", thaw: "Bliver en slimet, vandet masse", use: "Bør opbevares i køleskab i original emballage (ca. 1 uge jf. Tænk)" },
      { food: "Agurkesalat (syltet)", duration: "Uegnet til frost", pack: "Mister al sprødhed", thaw: "Bliver blød og gummiagtig", use: "Holder sig fint i køleskab uden frysning" }
    ],
    sections: [
      {
        h2: "Hvorfor agurken ikke tåler frost",
        body: `Agurk består af ca. 96 % vand bundet i et fint gitter af sprøde planteceller. Når agurk fryses ned, flænser iskrystallerne cellevæggene. Ved optøning siver væsken ud, og agurken forvandles til en mørkegrøn, slimet masse.

Ifølge Forbrugerrådet Tænk holder agurker sig bedst i den originale indpakning i køleskabet, hvor holdbarheden er cirka 1 uge. De bør aldrig fryses.`
      },
      {
        h2: "Squash til kødsovs og dahl",
        body: `Squash (courgette) er mere fiberrig og tåler frost langt bedre end agurk. Fødevarestyrelsen nævner udtrykkeligt squash som en af de bløde grøntsager, der kan fryses ned og tilsættes kødsovs eller dahl.

Riv squashen groft og frys den i flade poser, eller skær den i tern. Frossen squash kan kommes direkte i simrende retter uden optøning.`
      },
      {
        h2: "Holdbarhed på frost",
        body: `Ifølge Forbrugerrådet Tænks holdbarhedsoversigt kan grøntsager holde sig i fryseren i 6–12 måneder afhængigt af typen og emballagen.`
      }
    ],
    faq: [
      { q: "Hvorfor må man ikke fryse agurk?", a: "Agurk indeholder omkring 96 % vand. Frosten ødelægger cellerne, så agurken bliver slimet og vandet ved optøning." },
      { q: "Hvad kan frossen squash bruges til?", a: "Fødevarestyrelsen anbefaler at bruge frossen squash i kødsovs, dahl eller andre varme retter." },
      { q: "Hvor længe holder en agurk i køleskabet?", a: "Agurker holder cirka 1 uge i original indpakning i køleskabet ifølge Forbrugerrådet Tænk." },
      { q: "Hvor længe kan grøntsager ligge i fryseren?", a: "Grøntsager holder sig i 6–12 måneder på frost ifølge Forbrugerrådet Tænk." }
    ],
    internalLinks: [
      { text: "Fødevarer der ikke tåler frost", href: "/hvad-kan-man-ikke-fryse" },
      { text: "Sådan fryser du mad rigtigt", href: "/saadan-fryser-du-mad" },
      { text: "Holdbarhed for grøntsager i fryseren", href: "/hvor-laenge-holder-mad-i-fryseren" },
      { text: "Frysning af tomater og tomatpuré", href: "/tomater" },
      { text: "Frysning af kål og blomkål", href: "/kaal" }
    ],
    sources: [
      SOURCES.foedevarestyrelsenOpbevaring,
      SOURCES.taenkHoldbarhed
    ]
  },
  {
    slug: "kaal",
    shortTitle: "Kål og blomkål",
    title: "Kan man fryse kål?",
    metaTitle: "Kan man fryse kål? Hvidkål, spidskål og grønkål — Guide",
    metaDescription: "Ja, kål fryser godt og holder 6–12 mdr. jf. Tænk. Blanchering bevarer farve, bid og vitaminer. Læs om hvidkål, blomkål og grønkål.",
    answer: "Ja, alle kåltyper kan fryses; et kort opkog (blanchering) i 1–3 minutter sikrer, at kålen bevarer farve, sprødhed og næringsværdi på frost.",
    steps: [
      "Fjern de yderste visne blade, skyl kålen grundigt i koldt vand, og snit den i strimler eller buketter.",
      "Blanchér kålen i spilkogende vand (1 min for spidskål/grønkål, 2–3 min for hvidkål/blomkål).",
      "Køl straks kålen ned i en skål med iskoldt vand for at standse tilberedningen.",
      "Lad kålen dryppe grundigt af i et dørslag og dup den tør med et rent viskestykke.",
      "Pak kålen lufttæt i fryseposer og frys den fladt ned."
    ],
    tableNote: "Kilde: Fødevarestyrelsen og Forbrugerrådet Tænk",
    table: [
      { food: "Hvidkål og rødkål (snittet, blancheret)", duration: "6–12 måneder", pack: "Frysepose uden luft", thaw: "Direkte i gryde eller pande", use: "Grøntsager holder 6–12 mdr. jf. Tænk; gryderetter, stuvet kål" },
      { food: "Blomkål og broccoli (buketter)", duration: "6–12 måneder", pack: "Frosset løst på plade først", thaw: "Direkte i kogende vand (2–3 min)", use: "Fødevarestyrelsen nævner broccoli og blomkål til frost; supper, mos" },
      { food: "Grønkål (hakket blade)", duration: "6–12 måneder", pack: "Frysepose, let presset", thaw: "Direkte i gryde fra frost", use: "Klassisk grønlangkål, suppe, smoothies" },
      { food: "Kål i køleskab (frisk)", duration: "Flere uger", pack: "Plastpose i køleskab/altan", thaw: "Ikke relevant", use: "Holder flere uger i køleskab jf. Tænk" }
    ],
    sections: [
      {
        h2: "Hvorfor er blanchering en fordel for kål?",
        body: `Kål indeholder aktive plantenzymer, som fortsætter med at arbejde ved frostgrader, hvis de ikke inaktiveres. Ublancheret kål kan med tiden miste sin spændstighed og udvikle en kraftig kållugt.

Et kort opkog i kogende vand (blanchering) efterfulgt af hurtig nedkøling i isvand standser enzymerne, så kålens naturlige farvestoffer og vitaminer bevares bedre under frostlagringen.`
      },
      {
        h2: "Fødevarestyrelsens råd om hårde grøntsager",
        body: `Fødevarestyrelsen fremhæver netop hårde grøntsager som broccoli og blomkål som velegnede til fryseren.

En tommelfingerregel fra Fødevarestyrelsen er, at det du finder i frysedisken i supermarkedet, kan du også selv fryse ned derhjemme.`
      },
      {
        h2: "Holdbarhed i fryser og køleskab",
        body: `Ifølge Forbrugerrådet Tænk holder grøntsager sig i 6–12 måneder i fryseren, afhængigt af emballagen og grøntsagstypen.

I køleskabet eller på en kold altan kan frisk kål pakket i en plastpose holde sig i flere uger ifølge Tænk, mens et helt blomkålshoved holder cirka 1 måned i køleskabet.`
      }
    ],
    faq: [
      { q: "Hvor længe kan kål holde sig i fryseren?", a: "Grøntsager holder sig i 6–12 måneder på frost ifølge Forbrugerrådet Tænks holdbarhedstabel." },
      { q: "Hvor længe holder kål i køleskabet?", a: "Kål pakket i plastpose kan holde i flere uger på køl, og blomkål holder cirka 1 måned ifølge Forbrugerrådet Tænk." },
      { q: "Hvorfor skal kål blancheres før frysning?", a: "Kogning i 1–3 minutter inaktiverer enzymer, der ellers nedbryder farve, vitaminer og konsistens på frost." },
      { q: "Skal frossen kål tøs op før tilberedning?", a: "Nej, kom den frosne kål direkte i kogende vand, en varm suppe eller på panden." }
    ],
    internalLinks: [
      { text: "Sådan fryser du mad rigtigt", href: "/saadan-fryser-du-mad" },
      { text: "Holdbarhed for grøntsager i fryseren", href: "/hvor-laenge-holder-mad-i-fryseren" },
      { text: "Frysning af kartofler", href: "/kartofler" },
      { text: "Frysning af squash og agurk", href: "/squash-og-agurk" },
      { text: "Frysning af svampe", href: "/svampe" }
    ],
    sources: [
      SOURCES.foedevarestyrelsenOpbevaring,
      SOURCES.taenkHoldbarhed
    ]
  },
  {
    slug: "loeg-hvidloeg-ingefaer-chili",
    shortTitle: "Løg, hvidløg, ingefær og chili",
    title: "Kan man fryse løg, hvidløg, ingefær og chili?",
    metaTitle: "Kan man fryse løg, hvidløg, ingefær og chili? — Guide",
    metaDescription: "Ja, Fødevarestyrelsen og Samvirke anbefaler frysning af hvidløg og chili. Hak fint eller frys fladt. Se holdbarhed og råd her.",
    answer: "Ja, løg, hvidløg, frisk ingefær og chili fryser fremragende og kan tilføjes direkte i varme retter eller rives fra frossen tilstand.",
    steps: [
      "Fødevarestyrelsen råder til at hakke hvidløg og chili fint, inden de fryses ned.",
      "Samvirke anbefaler at fryse pillede hvidløgsfed hele eller hakket i et fladt, tyndt lag i en frysepose.",
      "Hak løg i tern og frys dem fladt i en frysepose; tag en håndfuld direkte på panden.",
      "Frys en hel ingefærknold med skræl; den kan rives direkte på rivejernet fra frossen tilstand.",
      "Kom de frosne aromater direkte i den varme gryde uden forudgående optøning."
    ],
    tableNote: "Kilde: Fødevarestyrelsen, Samvirke og Forbrugerrådet Tænk",
    table: [
      { food: "Hvidløg (finthakket eller i fed)", duration: "6–12 måneder", pack: "Fladt i pose jf. Samvirke", thaw: "Direkte på pande eller i gryde", use: "Fødevarestyrelsen og Samvirke anbefaler frysning; bræk et stykke af" },
      { food: "Chili (finthakket eller hel)", duration: "6–12 måneder", pack: "Frysepose", thaw: "Skæres direkte i ringe", use: "Fødevarestyrelsen råder til at hakke chili fint før frys" },
      { food: "Løg (hakkede tern)", duration: "6–12 måneder", pack: "Flad frysepose", thaw: "Direkte på varm pande", use: "Grøntsager holder 6–12 mdr. jf. Tænk; perfekt til kødsovs" },
      { food: "Frisk ingefær (hel knold)", duration: "6–12 måneder", pack: "Frysepose", thaw: "Rives direkte fra frossen", use: "Rives med skræl på rivejern direkte i retten" }
    ],
    sections: [
      {
        h2: "Fødevarestyrelsens og Samvirkes råd om hvidløg og chili",
        body: `Fødevarestyrelsen fremhæver udtrykkeligt hvidløg og chili som madvarer, man med fordel kan komme i fryseren, og anbefaler at hakke dem fint, inden de fryses ned.

Samvirke deler et praktisk tip: Pil hvidløgene og hak dem fint på én gang. Frys hvidløgene i et fladt, tyndt lag i en frysepose. Så kan du nemt brække en passende portion af direkte til madlavningen. Du kan også fryse hvidløgene hele og pillede i en zip-pose og tage op løbende.`
      },
      {
        h2: "Det geniale trick med frossen ingefær",
        body: `I køleskabet kan ingefær holde 2–3 uger i en plastpose ifølge Forbrugerrådet Tænk. Men har du købt en stor knold, er fryseren den bedste løsning.

Læg den hele ingefærknold direkte i fryseren i en frysepose. Når du skal bruge ingefær, tager du knolden ud og river den direkte på et fint rivejern med skrællen på. Fibrene findeles fint uden trævler, og resten af knolden lægges straks tilbage på frost.`
      },
      {
        h2: "Holdbarhed på frost og ved stuetemperatur",
        body: `Grøntsager holder generelt 6–12 måneder i fryseren ifølge Forbrugerrådet Tænk.

Ved stuetemperatur holder hvidløg 3–4 uger, og hele tørre løg kan holde op til et halvt år et køligt, mørkt og tørt sted jf. Tænk. Frysning af hakkede løg er dog en stor tidsbesparelse i hverdagen.`
      }
    ],
    faq: [
      { q: "Hvad råder Fødevarestyrelsen om frysning af hvidløg og chili?", a: "Fødevarestyrelsen råder til at hakke hvidløg og chili fint, inden de kommes i fryseren." },
      { q: "Hvordan fryser man hvidløg ifølge Samvirke?", a: "Samvirke anbefaler at hakke hvidløg fint og fryse dem i et fladt lag i en pose, så man let kan brække stykker af." },
      { q: "Kan man rive frossen ingefær med skræl?", a: "Ja, frossen ingefær kan rives direkte på rivejernet med skrællen på, uden at fibrene trævler." },
      { q: "Hvor længe kan grøntsager holde sig i fryseren?", a: "Grøntsager holder sig i 6–12 måneder på frost ifølge Forbrugerrådet Tænk." }
    ],
    internalLinks: [
      { text: "Sådan fryser du mad rigtigt", href: "/saadan-fryser-du-mad" },
      { text: "Holdbarhed for grønt i fryseren", href: "/hvor-laenge-holder-mad-i-fryseren" },
      { text: "Frysning af friske krydderurter", href: "/krydderurter" },
      { text: "Frysning af tomater og tomatpuré", href: "/tomater" },
      { text: "Frysning af kød og kødsovs", href: "/koed" }
    ],
    sources: [
      SOURCES.foedevarestyrelsenOpbevaring,
      SOURCES.samvirkeRester,
      SOURCES.taenkHoldbarhed
    ]
  },
  {
    slug: "svampe",
    shortTitle: "Svampe og champignon",
    title: "Kan man fryse svampe og champignon?",
    metaTitle: "Kan man fryse champignon og svampe? — Rå eller stegte",
    metaDescription: "Frys helst ikke svampe rå, da de bliver gummierede og svampede. Svits eller damp dem i smør først for det bedste resultat. Læs guiden.",
    answer: "Ja, men de bør ristes, svitses eller dampes let før frysning; rå svampe indeholder over 90 % vand og bliver gummiagtige og mørke på frost.",
    steps: [
      "Børst svampene rene for jord med en børste eller et stykke køkkenrulle; undgå at vaske dem i vand.",
      "Skær svampene i skiver eller kvarte.",
      "Svits svampene på en tør pande eller i en smule smør, indtil væsken fordamper (ca. 4–5 minutter).",
      "Køl svampene helt af i køleskabet før indfrysning.",
      "Pak dem i tætsluttende fryseposer og frys dem ned i passende portionsstørrelser."
    ],
    tableNote: "Kilde: Forbrugerrådet Tænk og Fødevarestyrelsen",
    table: [
      { food: "Champignon (tilberedte / ristede)", duration: "6–12 måneder", pack: "Frysepose uden luft", thaw: "Direkte på pande eller i gryde", use: "Grøntsager holder 6–12 mdr. jf. Tænk; bevarer fast struktur" },
      { food: "Champignon i køleskab (friske)", duration: "Cirka 1 uge", pack: "Original indpakning i køleskab", thaw: "Ikke relevant", use: "Holder cirka 1 uge i køleskab ifølge Tænk" },
      { food: "Rå champignon (frosset rå)", duration: "Uegnet til rå frys", pack: "Mørkner og bliver bløde", thaw: "Direkte på meget varm pande", use: "Bliver svampede og udskiller store mængder vand" }
    ],
    sections: [
      {
        h2: "Hvorfor bliver rå svampe gummiagtige?",
        body: `Svampe består af over 90 % vand og har en kitinholdig cellestruktur. Når rå champignoner fryses ned, gennemborer iskrystallerne cellestrukturen, og enzymer i svampen får overfladen til at oxidere og blive mørk.

Ved optøning kollapser strukturen, og svampen udskiller store mængder vand, så den ender med at koge i sin egen væske frem for at stege. Konsistensen bliver let sej og gummiagtig.`
      },
      {
        h2: "Svitsning før frysning forsegler smagen",
        body: `Den bedste metode til at gemme svampe i fryseren er at tilberede dem let på forhånd. Skær svampene i skiver og kom dem på en varm pande med en klat smør eller olie.

Lad svampene stege i 4–5 minutter, indtil de afgiver deres væske og væsken igen fordamper. Herved inaktiveres enzymerne, vandet reduceres, og svampens naturlige umamismag koncentreres. Lad dem køle helt af, inden de pakkes i fryseposer.`
      },
      {
        h2: "Holdbarhed i køleskab og fryser",
        body: `Ifølge Forbrugerrådet Tænk holder champignoner længst i deres indpakning i køleskabet, hvor de kan holde sig cirka 1 uge. På frost holder grøntsager i 6–12 måneder jf. Tænks holdbarhedstabel.`
      }
    ],
    faq: [
      { q: "Kan man fryse rå champignon?", a: "Det frarådes; de bliver mørke, gummiagtige og udskiller meget vand. Steg eller damp dem kort først for det bedste resultat." },
      { q: "Hvor længe holder champignoner i køleskabet?", a: "Champignoner holder cirka 1 uge i deres indpakning i køleskabet ifølge Forbrugerrådet Tænk." },
      { q: "Hvor længe kan tilberedte svampe holde sig i fryseren?", a: "Grøntsager holder sig i 6–12 måneder ved -18 °C ifølge Forbrugerrådet Tænk." },
      { q: "Skal frosne svampe tøs op før brug?", a: "Nej, kom de tilberedte frosne svampe direkte i den simrende gryderet, sauce eller på panden." }
    ],
    internalLinks: [
      { text: "Sådan fryser du mad rigtigt", href: "/saadan-fryser-du-mad" },
      { text: "Holdbarhedstabeller for madvarer", href: "/hvor-laenge-holder-mad-i-fryseren" },
      { text: "Frysning af løg og hvidløg", href: "/loeg-hvidloeg-ingefaer-chili" },
      { text: "Frysning af krydderurter", href: "/krydderurter" },
      { text: "Frysning af kål og grøntsager", href: "/kaal" }
    ],
    sources: [
      SOURCES.foedevarestyrelsenOpbevaring,
      SOURCES.taenkHoldbarhed
    ]
  },
  {
    slug: "krydderurter",
    shortTitle: "Krydderurter",
    title: "Kan man fryse krydderurter?",
    metaTitle: "Kan man fryse persille, dild, purløg og basilikum? — Guide",
    metaDescription: "Fødevarestyrelsen anbefaler at hakke friske krydderurter fint før frys. Læs om frysning, opbevaring og køkkenhygiejne her.",
    answer: "Ja, friske krydderurter kan fint fryses ned; Fødevarestyrelsen råder til at hakke dem fint, inden de kommes i fryseren.",
    steps: [
      "Fødevarestyrelsen anbefaler at hakke friske krydderurter fint, inden de fryses ned.",
      "Skyl krydderurterne yderst grundigt i koldt vand for at fjerne enhver rest af jord.",
      "Tør urterne helt i en salatslynge eller på et rent viskestykke; våde urter klumper sammen.",
      "Frys de finthakkede urter i små plastbøtter eller glas, så du let kan tage en skefuld med en gaffel.",
      "Samvirke anbefaler at fryse basilikum sammen med tomater til senere tomatsovs."
    ],
    tableNote: "Kilde: Fødevarestyrelsen, Forbrugerrådet Tænk og Samvirke",
    table: [
      { food: "Friske krydderurter (persille, dild, purløg)", duration: "6–12 måneder", pack: "Skyllet, tørret, finthakket i bøtte/glas", thaw: "Tages med gaffel direkte fra frossen", use: "Fødevarestyrelsen råder til finthakning før frys; 6–12 mdr. jf. Tænk" },
      { food: "Afskårne urter i køleskab", duration: "3–5 dage", pack: "Plastpose i køleskab", thaw: "Ikke relevant", use: "Holder 3–5 dage i køleskab ifølge Forbrugerrådet Tænk" },
      { food: "Basilikum med tomater", duration: "6–12 måneder", pack: "Frysepose", thaw: "Direkte i gryde", use: "Samvirke anbefaler at fryse basilikumblade sammen med tomater til sauce" }
    ],
    sections: [
      {
        h2: "Fødevarestyrelsens råd om krydderurter på frost",
        body: `Fødevarestyrelsen nævner specifikt friske krydderurter som en fødevare, man med fordel kan komme i fryseren, og anbefaler at hakke dem fint, inden de fryses ned.

Da krydderurter gror tæt ved jorden, minder Fødevarestyrelsen om god køkkenhygiejne: Skyl altid krydderurterne grundigt for at fjerne jordbakterier. Bruges frossen persille i en sovs, bør sovsen koge godt igennem.`
      },
      {
        h2: "Holdbarhed i køleskab vs. fryser",
        body: `Ifølge Forbrugerrådet Tænk holder afskårne krydderurter kun 3–5 dage i køleskabet i en pose. Frysning forlænger holdbarheden væsentligt – grøntsager og urter holder 6–12 måneder på frost ifølge Tænk.`
      },
      {
        h2: "Frysning i tørt glas eller med tomater",
        body: `Vask urterne og kør dem grundigt i en salatslynge, og lad dem tørre helt på et viskestykke. Hak dem fint og kom dem i et tørt sylteglas med låg. Da bladene er tørre, fryser de ikke sammen i en massiv klump, men kan skrabes ud med en gaffel.

Samvirke foreslår desuden at fryse triste basilikumblade i samme frysepose som overskydende bløde tomater – så har du den perfekte base klar til en pastasovs.`
      }
    ],
    faq: [
      { q: "Hvad anbefaler Fødevarestyrelsen om frysning af krydderurter?", a: "Fødevarestyrelsen anbefaler at hakke friske krydderurter fint, inden de fryses ned." },
      { q: "Hvor længe holder friske krydderurter i køleskabet?", a: "Afskårne krydderurter kan holde sig 3–5 dage i en pose i køleskabet ifølge Forbrugerrådet Tænk." },
      { q: "Hvor længe kan frosne krydderurter holde sig?", a: "I fryseren holder grøntsager og urter i 6–12 måneder ifølge Forbrugerrådet Tænks oversigt." },
      { q: "Hvordan undgår man, at frosne urter klumper sammen?", a: "Urterne skal tørres fuldstændigt efter vask, før de hakkes og fryses i en tør beholder." }
    ],
    internalLinks: [
      { text: "Sådan fryser du mad rigtigt", href: "/saadan-fryser-du-mad" },
      { text: "Holdbarhed for mad i fryseren", href: "/hvor-laenge-holder-mad-i-fryseren" },
      { text: "Frysning af løg og hvidløg", href: "/loeg-hvidloeg-ingefaer-chili" },
      { text: "Frysning af tomater", href: "/tomater" },
      { text: "Frysning af saucer og pesto", href: "/saucer-og-salater" }
    ],
    sources: [
      SOURCES.foedevarestyrelsenOpbevaring,
      SOURCES.taenkHoldbarhed,
      SOURCES.samvirkeRester
    ]
  }
];
